import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThumbsUp, ThumbsDown, Loader2, RefreshCw, AlertTriangle, BarChart3, MessageSquare, Search, FlaskConical, FileText, User, Bot, MessageCircle, Wrench, CheckCircle2, XCircle, Download, Mail, Package, Home, Activity, Database, ArrowUpRight, ArrowDownRight, Minus, Briefcase, Star } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAgents } from '../hooks/useAgents';
import TestResults from '../components/TestResults';
import TestReviewInterface from '../components/TestReviewInterface';
import AgentAnalytics from '../components/AgentAnalytics';
import AgentWhatsNewEditor from '../components/AgentWhatsNewEditor';
import ComprehensiveAnalytics from '../components/ComprehensiveAnalytics';
import { IndexingDashboard } from './admin/IndexingDashboard';
import { TestRun, TestResult, FeedbackEntry } from '../types';
import { testResultsService } from '../services/testResultsService';
import { appConfig } from '../config/appConfig';
import OidcAuthService from '../services/oidcAuth';
import './AgentDashboard.css';

// Backend API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api` 
  : '/api';

interface AgentConfig {
  id: string;
  displayName: string;
  icon: string;
  color: string;
  description: string;
  hasTestResults: boolean;
}

interface AgentDashboardProps {
  agentConfig: AgentConfig;
}

type DashboardTab = 'interactions' | 'analytics' | 'testing' | 'test-review' | 'indexing' | 'whats-new';

// Unified interaction with optional feedback
interface UnifiedInteraction {
  id: string;
  partitionKey: string;
  userId: string;
  agentId: string;
  agentName: string;
  title: string;
  lastUserMessage: string;
  lastAgentMessage: string;
  messageCount: number;
  messages: any[] | null;  // null = not loaded yet, empty array = loaded but no messages
  createdAt: string;
  updatedAt: string;
  products?: {
    styles: string[];
    names: string[];
    skus: string[];
    count: number;
  };
  hasProductData: boolean;
  feedback?: {
    type: 'positive' | 'negative';
    tags: string[];
    notes: string;
    uid: string;
    created_at: string;
  };
  hasFeedback: boolean;
}

interface InteractionStats {
  total: number;
  positive: number;
  negative: number;
  noFeedback: number;
  positiveRate: number;
}

const PctBadge: React.FC<{ current: number; previous: number; mode?: 'relative' | 'points' }> = ({ current, previous, mode = 'relative' }) => {
  if (previous === 0 && current === 0) return null;
  if (mode === 'points') {
    const diff = Math.round(current - previous);
    if (diff === 0) return <span className="interactions-pct-badge interactions-pct-flat"><Minus size={10} /> 0 pts</span>;
    const isUp = diff > 0;
    return (
      <span className={`interactions-pct-badge ${isUp ? 'interactions-pct-up' : 'interactions-pct-down'}`}>
        {isUp ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
        {isUp ? '+' : ''}{diff} pts
      </span>
    );
  }
  if (previous === 0) return <span className="interactions-pct-badge interactions-pct-new">new</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return <span className="interactions-pct-badge interactions-pct-flat"><Minus size={10} /> 0%</span>;
  const isUp = pct > 0;
  return (
    <span className={`interactions-pct-badge ${isUp ? 'interactions-pct-up' : 'interactions-pct-down'}`}>
      {isUp ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {isUp ? '+' : ''}{pct}%
    </span>
  );
};

const AgentDashboard: React.FC<AgentDashboardProps> = ({ agentConfig }) => {
  const navigate = useNavigate();
  const { hasAdminAccess, user, hasFullAdminAccess } = useAuth();
  const { allAgents } = useAgents();
  
  // Check if user is super admin based on roles
  const userRoles = (user as any)?.userRoles || [];
  const isSuperAdmin = userRoles.some((role: string) => 
    role === 'admin' || role === 'super-admin' || role === 'askaldo-admin'
  ) || hasFullAdminAccess();
  const [activeTab, setActiveTab] = useState<DashboardTab>('analytics');
  const isAggregateDashboard = agentConfig.id === 'aldo';
  const [selectedAgentId, setSelectedAgentId] = useState<string>('all');
  
  // Unified Interactions state (combines logs + feedback)
  const [interactions, setInteractions] = useState<UnifiedInteraction[]>([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [interactionsError, setInteractionsError] = useState<string | null>(null);
  const [interactionStats, setInteractionStats] = useState<InteractionStats>({
    total: 0, positive: 0, negative: 0, noFeedback: 0, positiveRate: 0
  });
  const [prevInteractionStats, setPrevInteractionStats] = useState<InteractionStats | null>(null);
  const [interactionsTimeWindow, setInteractionsTimeWindow] = useState<TimeWindow>('all');
  const [interactionsFeedbackFilter, setInteractionsFeedbackFilter] = useState<'all' | 'positive' | 'negative' | 'none'>('all');
  const [interactionsSearchTerm, setInteractionsSearchTerm] = useState<string>('');
  const [interactionsJobCode, setInteractionsJobCode] = useState<string>('');
  const [availableJobCodes, setAvailableJobCodes] = useState<string[]>([]);
  const [expandedInteractionIds, setExpandedInteractionIds] = useState<Set<string>>(new Set());
  const [loadingMessagesIds, setLoadingMessagesIds] = useState<Set<string>>(new Set());
  const [hasMoreInteractions, setHasMoreInteractions] = useState<boolean>(true);
  const [loadingMoreInteractions, setLoadingMoreInteractions] = useState<boolean>(false);
  const interactionsListRef = React.useRef<HTMLDivElement>(null);
  const interactionsOffsetRef = React.useRef<number>(0);
  const isLoadingMoreRef = React.useRef<boolean>(false);
  const INTERACTIONS_PAGE_SIZE = 100;
  type TimeWindow = 'today' | 'week' | 'month' | '6months' | 'all';

  const agentFilterOptions = useMemo(() => {
    if (!isAggregateDashboard) return [];
    return allAgents
      .filter(agent => !agent.hidden)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allAgents, isAggregateDashboard]);
  
  // Lazy load messages for a specific interaction
  const loadMessagesForInteraction = async (interactionId: string, userId: string, agentId: string) => {
    // Skip if already loading or already loaded
    if (loadingMessagesIds.has(interactionId)) return;
    const existing = interactions.find(i => i.id === interactionId);
    if (existing?.messages !== null) return;
    
    setLoadingMessagesIds(prev => new Set(prev).add(interactionId));
    
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_BASE_URL}/conversation-history/interactions/${interactionId}/messages?userId=${encodeURIComponent(userId)}&agentId=${encodeURIComponent(agentId)}`,
        { headers }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.messages) {
          // Update the interaction with loaded messages
          setInteractions(prev => prev.map(i => 
            i.id === interactionId ? { ...i, messages: data.messages } : i
          ));
        }
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoadingMessagesIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(interactionId);
        return newSet;
      });
    }
  };
  
  // Helper function to get authentication headers for API requests
  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    const oidcService = OidcAuthService.getInstance();
    
    try {
      await oidcService.ensureInitialized();
      // Prefer ID token (contains app roles)
      let token = await oidcService.getIdToken();
      
      // Fall back to access token if ID token missing
      if (!token) {
        token = await oidcService.getAccessToken();
      }
      
      if (token) {
        return {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
      }
    } catch (error) {
      console.error('Failed to get auth token:', error);
    }
    
    return { 'Content-Type': 'application/json' };
  };
  
  // Feedback state
  const [feedbackEntries, setFeedbackEntries] = useState<FeedbackEntry[]>([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [feedbackStats, setFeedbackStats] = useState({ positive: 0, negative: 0, total: 0 });
  // Map display names to backend agent names for feedback filtering
  const getBackendAgentName = (agentId: string): string => {
    // Try multiple possible agent names that might be stored in the backend
    // IMPORTANT: First name should be the agent ID (what's actually stored in feedback)
    const agentNameMap: Record<string, string[]> = {
      'aldo': ['aldo', 'Aldo', 'Scout', 'aldo-unified-agent'],
      'hrpolicy': ['hrpolicy', 'HR Policy Agent', 'hr-policy-agent-webapp', 'PolicyTech Policy Agent', 'Policy Agent'],
      'pwms-support': ['pwms-support', 'PWMS Support Agent', 'pwms_support', 'PWMS Agent', 'pwms_support_agent'],
      'product': ['product', 'Product', 'Product Agent']
    };
    
    // Return the first/primary name for the agent (should be the agent ID)
    const possibleNames = agentNameMap[agentId];
    return possibleNames?.[0] || agentConfig.displayName;
  };

  const [feedbackFilters, setFeedbackFilters] = useState({
    agent: getBackendAgentName(agentConfig.id),
    sortBy: 'created_at',
    sortOrder: 'desc' as 'asc' | 'desc',
    limit: 50,
    timeWindow: 'all' as TimeWindow
  });
  const [totalFeedbackCount, setTotalFeedbackCount] = useState(0);
  const [expandedFeedbackIds, setExpandedFeedbackIds] = useState<Set<string>>(new Set());
  
  // Logs state
  const [logEntries, setLogEntries] = useState<any[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsLimit, setLogsLimit] = useState<number>(50);
  const [logsTimeWindow, setLogsTimeWindow] = useState<TimeWindow>('all');
  const [expandedLogIds, setExpandedLogIds] = useState<Set<string>>(new Set());
  const [logsSearchTerm, setLogsSearchTerm] = useState<string>('');
  const [logsSearchType, setLogsSearchType] = useState<'all' | 'user' | 'agent'>('all');
  const [totalLogsUnfiltered, setTotalLogsUnfiltered] = useState<number>(0);
  
  // Testing state
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [currentTestRun, setCurrentTestRun] = useState<TestRun | undefined>();
  const [testRunError, setTestRunError] = useState<string | null>(null);
  const [availableCsvFiles, setAvailableCsvFiles] = useState<{filename: string, displayName: string, description: string}[]>([]);
  const [selectedCsvFile, setSelectedCsvFile] = useState<string>('');
  const [realTestResults, setRealTestResults] = useState<TestResult[]>([]);
  const [csvLoadError, setCsvLoadError] = useState<string | null>(null);
  const [loadingCsvFiles, setLoadingCsvFiles] = useState<boolean>(true);
  const [loadingCsvData, setLoadingCsvData] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  
  // Test Review state
  const [mostRecentTestRun, setMostRecentTestRun] = useState<TestRun | null>(null);
  const [loadingMostRecent, setLoadingMostRecent] = useState(false);
  
  // Request deduplication to prevent 409 conflicts
  const loadingRequestRef = React.useRef<AbortController | null>(null);

  // Load most recent test run for Test Review tab
  const loadMostRecentTestRun = useCallback(async () => {
    if (!agentConfig.hasTestResults) return;
    
    setLoadingMostRecent(true);
    try {
      // Load available CSV files
      const csvFiles = await testResultsService.getAvailableCsvFiles();
      
      // Filter CSV files based on agent (same logic as loadTestRuns)
      let filteredFiles = csvFiles;
      if (agentConfig.id === 'hrpolicy') {
        filteredFiles = csvFiles.filter(file => 
          file.filename.includes('policy') || 
          file.displayName.toLowerCase().includes('policy') ||
          file.displayName.toLowerCase().includes('hr')
        );
      } else if (agentConfig.id === 'pwms-support') {
        filteredFiles = csvFiles.filter(file => 
          file.filename.includes('pwms') || 
          file.displayName.toLowerCase().includes('pwms')
        );
      }
      
      if (filteredFiles.length > 0) {
        // Use the first available file (they're typically sorted by date)
        const mostRecentFile = filteredFiles[0];
        const results = await testResultsService.loadCsvFile(mostRecentFile.filename);
        
        // Create a TestRun object from the loaded data
        if (results.length > 0) {
          const testRun: TestRun = {
            id: mostRecentFile.filename,
            filename: mostRecentFile.filename,
            timestamp: new Date().toISOString(),
            endpoint: agentConfig.displayName,
            total_questions: results.length,
            satisfactory_count: results.filter(r => r.is_satisfactory).length,
            unsatisfactory_count: results.filter(r => !r.is_satisfactory).length,
            results: results
          };
          setMostRecentTestRun(testRun);
        }
      }
    } catch (error) {
      console.error('Failed to load most recent test run:', error);
    } finally {
      setLoadingMostRecent(false);
    }
  }, [agentConfig.id, agentConfig.hasTestResults, agentConfig.displayName]);

  const handleGoHome = () => {
    navigate('/');
  };

  // Load feedback data
  const loadFeedback = useCallback(async (customFilters?: Partial<typeof feedbackFilters>) => {
    if (!hasAdminAccess()) return;
    
    // Cancel any existing request to prevent conflicts
    if (loadingRequestRef.current) {
      loadingRequestRef.current.abort();
    }
    
    // Create new abort controller for this request
    const abortController = new AbortController();
    loadingRequestRef.current = abortController;
    
    setLoadingFeedback(true);
    setFeedbackError(null);
    
    try {
      const filters = { ...feedbackFilters, ...customFilters };
      console.log(`🔍 Loading feedback for ${agentConfig.displayName} (ID: ${agentConfig.id})...`, filters);
      
      const queryParams = new URLSearchParams({
        agent: filters.agent,
        limit: filters.limit.toString(),
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        includeHistory: 'true' // Request enhanced feedback data with full conversation history
      });
      
      // Add time window filter
      const startDate = getStartDateFromTimeWindow(filters.timeWindow);
      if (startDate) {
        queryParams.append('startDate', startDate);
      }

      const requestUrl = `${API_BASE_URL}/feedback/entries?${queryParams}`;
      console.log(`📡 Making request to: ${requestUrl}`);

      // Get authentication headers
      const headers = await getAuthHeaders();
      
      const response = await fetch(requestUrl, {
        signal: abortController.signal,
        headers
      });
      
      if (!response.ok) {
        console.error(`❌ HTTP Error ${response.status}: ${response.statusText} for agent: ${filters.agent}`);
        if (response.status === 409) {
          throw new Error(`Conflict error (409): There may be a duplicate request in progress for ${filters.agent}. Please try again in a moment.`);
        }
        throw new Error(`Failed to load feedback: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setFeedbackEntries(data.entries || []);
        setTotalFeedbackCount(data.totalCount || 0);
        
        console.log(`📊 Agent summary from API:`, data.agentSummary);
        console.log(`🎯 Looking for agent key: "${filters.agent}"`);
        console.log(`📋 Available keys:`, Object.keys(data.agentSummary || {}));
        
        // Try to find stats with different agent name variations
        let agentStats = data.agentSummary?.[filters.agent];
        if (!agentStats && agentConfig.id === 'hrpolicy') {
          // Try different possible names for HR Policy agent
          const possibleNames = ['HR Policy Agent', 'hrpolicy', 'hr-policy-agent-webapp', 'PolicyTech Policy Agent'];
          for (const name of possibleNames) {
            if (data.agentSummary?.[name]) {
              agentStats = data.agentSummary[name];
              console.log(`✅ Found stats under agent name: "${name}"`);
              break;
            }
          }
        }
        
        setFeedbackStats(agentStats || { positive: 0, negative: 0, total: 0 });
        
        console.log(`✅ Loaded ${data.entries?.length || 0} feedback entries for ${agentConfig.displayName}`);
      } else {
        throw new Error(data.error || 'Failed to load feedback');
      }
      
    } catch (error: any) {
      // Don't show error if request was aborted (user initiated new request)
      if (error?.name === 'AbortError') {
        console.log('🛑 Feedback request aborted (new request started)');
        return;
      }
      
      console.error('Failed to load feedback:', error);
      let errorMessage = 'Failed to load feedback data';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to feedback service. Please check your internet connection.';
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Access denied. You may not have permission to view this feedback data.';
        } else if (error.message.includes('404')) {
          errorMessage = 'No feedback data found for this agent.';
        } else if (error.message.includes('409')) {
          errorMessage = 'Conflict error: There may be a duplicate request. Please wait and try again.';
        } else if (error.message.includes('500')) {
          errorMessage = 'Server error while loading feedback. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setFeedbackError(errorMessage);
    } finally {
      // Clear the loading request ref
      if (loadingRequestRef.current === abortController) {
        loadingRequestRef.current = null;
      }
      setLoadingFeedback(false);
    }
  }, [agentConfig.displayName, agentConfig.id, feedbackFilters, hasAdminAccess]);

  // Helper function to calculate start date from time window
  const getStartDateFromTimeWindow = (timeWindow: TimeWindow): string | null => {
    const now = new Date();
    switch (timeWindow) {
      case 'today':
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return today.toISOString();
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return weekAgo.toISOString();
      case 'month':
        const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        return monthAgo.toISOString();
      case '6months':
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        return sixMonthsAgo.toISOString();
      case 'all':
      default:
        return null;
    }
  };

  const getPreviousPeriodDates = (timeWindow: TimeWindow): { start: string; end: string } | null => {
    const now = new Date();
    switch (timeWindow) {
      case 'today': {
        const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        const yesterdayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return { start: yesterday.toISOString(), end: yesterdayEnd.toISOString() };
      }
      case 'week': {
        const prevEnd = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const prevStart = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
        return { start: prevStart.toISOString(), end: prevEnd.toISOString() };
      }
      case 'month': {
        const prevEnd = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        const prevStart = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
        return { start: prevStart.toISOString(), end: prevEnd.toISOString() };
      }
      case '6months': {
        const prevEnd = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        const prevStart = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
        return { start: prevStart.toISOString(), end: prevEnd.toISOString() };
      }
      default:
        return null;
    }
  };

  // Load unified interactions (combines logs with feedback)
  const loadInteractions = useCallback(async (append: boolean = false) => {
    if (!hasAdminAccess()) return;
    
    // Prevent duplicate loads
    if (append && isLoadingMoreRef.current) return;
    
    if (append) {
      isLoadingMoreRef.current = true;
      setLoadingMoreInteractions(true);
    } else {
      interactionsOffsetRef.current = 0;
      setLoadingInteractions(true);
      setHasMoreInteractions(true);
    }
    setInteractionsError(null);
    
    try {
      const offset = append ? interactionsOffsetRef.current : 0;
      const params = new URLSearchParams({
        limit: INTERACTIONS_PAGE_SIZE.toString(),
        offset: offset.toString(),
        search: interactionsSearchTerm,
        searchType: 'all',
        feedbackFilter: interactionsFeedbackFilter,
        ...(interactionsJobCode ? { jobCode: interactionsJobCode } : {})
      });
      const effectiveAgentId = isAggregateDashboard
        ? (selectedAgentId === 'all' ? null : selectedAgentId)
        : agentConfig.id;
      if (effectiveAgentId) {
        params.append('agentId', effectiveAgentId);
      }
      
      // Add time window filter
      const startDate = getStartDateFromTimeWindow(interactionsTimeWindow);
      if (startDate) {
        params.append('startDate', startDate);
      }

      const headers = await getAuthHeaders();
      
      // Build previous-period request (stats only, small limit) if not appending
      let prevPromise: Promise<Response | null> = Promise.resolve(null);
      if (!append) {
        const prevDates = getPreviousPeriodDates(interactionsTimeWindow);
        if (prevDates) {
          const prevParams = new URLSearchParams({ limit: '1', offset: '0', startDate: prevDates.start, endDate: prevDates.end });
          if (effectiveAgentId) prevParams.append('agentId', effectiveAgentId);
          if (interactionsJobCode) prevParams.append('jobCode', interactionsJobCode);
          prevPromise = fetch(`${API_BASE_URL}/conversation-history/interactions?${prevParams}`, { headers }).catch(() => null);
        }
      }

      const [response, prevResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/conversation-history/interactions?${params}`, { headers }),
        prevPromise
      ]);
      
      if (!response.ok) {
        throw new Error(`Failed to load interactions: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Parse previous period stats
      if (!append && prevResponse && prevResponse.ok) {
        try {
          const prevData = await prevResponse.json();
          if (prevData.success && prevData.stats) {
            setPrevInteractionStats(prevData.stats);
          } else {
            setPrevInteractionStats(null);
          }
        } catch { setPrevInteractionStats(null); }
      } else if (!append) {
        setPrevInteractionStats(null);
      }

      if (data.success) {
        const newInteractions = data.interactions || [];
        
        if (append) {
          setInteractions(prev => {
            const updated = [...prev, ...newInteractions];
            interactionsOffsetRef.current = updated.length;
            return updated;
          });
        } else {
          setInteractions(newInteractions);
          interactionsOffsetRef.current = newInteractions.length;
          setInteractionStats(data.stats || { total: 0, positive: 0, negative: 0, noFeedback: 0, positiveRate: 0 });
        }
        
        // Check if there are more to load (use API response if available, otherwise estimate)
        setHasMoreInteractions(data.hasMore ?? newInteractions.length >= INTERACTIONS_PAGE_SIZE);
        
        console.log(`✅ Loaded ${newInteractions.length} interactions${append ? ' (more)' : ''} for ${agentConfig.displayName}`, data.stats);
      } else {
        throw new Error(data.error || 'Failed to load interactions');
      }
      
    } catch (error: any) {
      console.error('Failed to load interactions:', error);
      setInteractionsError(error instanceof Error ? error.message : 'Failed to load interactions');
    } finally {
      setLoadingInteractions(false);
      setLoadingMoreInteractions(false);
      isLoadingMoreRef.current = false;
    }
  }, [agentConfig.id, agentConfig.displayName, interactionsSearchTerm, interactionsTimeWindow, interactionsFeedbackFilter, interactionsJobCode, hasAdminAccess, isAggregateDashboard, selectedAgentId]);

  // Load more interactions (infinite scroll)
  const loadMoreInteractions = useCallback(() => {
    if (!isLoadingMoreRef.current && hasMoreInteractions && !loadingInteractions) {
      loadInteractions(true);
    }
  }, [loadInteractions, hasMoreInteractions, loadingInteractions]);

  // Load logs (conversation interactions)
  const loadLogs = useCallback(async () => {
    if (!hasAdminAccess()) return;
    
    setLoadingLogs(true);
    setLogsError(null);
    
    try {
      const params = new URLSearchParams({
        limit: logsLimit === 0 ? '10000' : logsLimit.toString(), // 0 means all
        search: logsSearchTerm,
        searchType: logsSearchType
      });
      const effectiveAgentId = isAggregateDashboard
        ? (selectedAgentId === 'all' ? null : selectedAgentId)
        : agentConfig.id;
      if (effectiveAgentId) {
        params.append('agentId', effectiveAgentId);
      }
      
      // Add time window filter
      const startDate = getStartDateFromTimeWindow(logsTimeWindow);
      if (startDate) {
        params.append('startDate', startDate);
      }

      // Get authentication headers
      const headers = await getAuthHeaders();
      
      const response = await fetch(`${API_BASE_URL}/conversation-history/logs?${params}`, {
        headers
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load logs: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setLogEntries(data.logs || []);
        setTotalLogsUnfiltered(data.totalUnfiltered || data.total || 0);
        
        const searchInfo = logsSearchTerm 
          ? ` (filtered from ${data.totalUnfiltered} by "${logsSearchTerm}" in ${logsSearchType} messages)`
          : '';
        console.log(`✅ Loaded ${data.logs?.length || 0} log entries for ${agentConfig.displayName}${searchInfo}`);
      } else {
        throw new Error(data.error || 'Failed to load logs');
      }
      
    } catch (error: any) {
      console.error('Failed to load logs:', error);
      setLogsError(error instanceof Error ? error.message : 'Failed to load logs');
    } finally {
      setLoadingLogs(false);
    }
  }, [agentConfig.id, agentConfig.displayName, logsLimit, logsSearchTerm, logsSearchType, logsTimeWindow, hasAdminAccess, isAggregateDashboard, selectedAgentId]);

  // Load test runs
  const loadTestRuns = useCallback(async () => {
    if (!hasAdminAccess() || !agentConfig.hasTestResults) return;
    
    try {
      console.log(`Loading test runs for ${agentConfig.displayName}...`);
      
      // Load available CSV files
      setLoadingCsvFiles(true);
      const csvFiles = await testResultsService.getAvailableCsvFiles();
      
      // Filter CSV files based on agent (if needed)
      let filteredFiles = csvFiles;
      if (agentConfig.id === 'hrpolicy') {
        // HR Policy agent should only see policy-related files
        filteredFiles = csvFiles.filter(file => 
          file.filename.includes('policy') || 
          file.displayName.toLowerCase().includes('policy') ||
          file.displayName.toLowerCase().includes('hr')
        );
      } else if (agentConfig.id === 'pwms-support') {
        // PWMS agent should only see PWMS-related files
        filteredFiles = csvFiles.filter(file => 
          file.filename.includes('pwms') || 
          file.displayName.toLowerCase().includes('pwms')
        );
      }
      
      setAvailableCsvFiles(filteredFiles);
      
      // Auto-select first file if none selected
      if (filteredFiles.length > 0 && !selectedCsvFile) {
        setSelectedCsvFile(filteredFiles[0].filename);
      }
      
      setLoadingCsvFiles(false);
      
    } catch (error) {
      console.error('Failed to load test runs:', error);
      setTestRunError('Failed to load test runs');
      setLoadingCsvFiles(false);
    }
  }, [agentConfig.hasTestResults, agentConfig.displayName, agentConfig.id, hasAdminAccess, selectedCsvFile]);

  // Load test run data
  const loadTestRun = useCallback(async (filename: string): Promise<TestRun> => {
    return await testResultsService.loadTestRun(filename);
  }, []);

  // Analyze test results
  const analyzeTestResults = useCallback(async (prompt: string, results: TestResult[]): Promise<string> => {
    return await testResultsService.analyzeResults(prompt, results);
  }, []);

  // Load CSV data when file is selected
  useEffect(() => {
    if (selectedCsvFile && agentConfig.hasTestResults) {
      setLoadingCsvData(true);
      setCsvLoadError(null);
      
      testResultsService.loadCsvFile(selectedCsvFile)
        .then(results => {
          setRealTestResults(results);
          setLoadingCsvData(false);
          
          // Create a mock test run from the loaded data
          if (results.length > 0) {
            const mockTestRun: TestRun = {
              id: selectedCsvFile,
              filename: selectedCsvFile,
              timestamp: new Date().toISOString(),
              endpoint: agentConfig.displayName,
              total_questions: results.length,
              satisfactory_count: results.filter(r => r.is_satisfactory).length,
              unsatisfactory_count: results.filter(r => !r.is_satisfactory).length,
              results: results
            };
            setTestRuns([mockTestRun]);
            setCurrentTestRun(mockTestRun);
          }
        })
        .catch(error => {
          console.error('Failed to load CSV data:', error);
          setCsvLoadError(error.message);
          setLoadingCsvData(false);
        });
    }
  }, [selectedCsvFile, agentConfig.hasTestResults, agentConfig.displayName, availableCsvFiles]);

  // Cleanup effect to abort requests when component unmounts or agent changes
  useEffect(() => {
    return () => {
      if (loadingRequestRef.current) {
        loadingRequestRef.current.abort();
        loadingRequestRef.current = null;
      }
    };
  }, [agentConfig.id]);

  // Load initial data
  useEffect(() => {
    if (activeTab === 'interactions') {
      loadInteractions();
    } else if (activeTab === 'testing' && agentConfig.hasTestResults) {
      loadTestRuns();
    } else if (activeTab === 'test-review' && agentConfig.hasTestResults) {
      loadMostRecentTestRun();
    }
  }, [activeTab, loadInteractions, loadTestRuns, loadMostRecentTestRun, agentConfig.hasTestResults]);

  // Fetch available job codes for interactions filter
  useEffect(() => {
    fetch(`${API_BASE_URL}/analytics/job-codes?days=90`)
      .then(r => r.json())
      .then(data => {
        if (!data.success || !data.jobCodes?.length) return;
        // The endpoint returns {code, title} objects; this filter works on the
        // bare code. Rendering an object as an <option> child crashes the page.
        setAvailableJobCodes(
          data.jobCodes.map((jc: string | { code: string }) => (typeof jc === 'string' ? jc : String(jc.code)))
        );
      })
      .catch(() => {});
  }, []);

  // Reload interactions when filters change (reset to first page)
  useEffect(() => {
    if (activeTab === 'interactions') {
      loadInteractions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactionsSearchTerm, interactionsTimeWindow, interactionsFeedbackFilter, interactionsJobCode, activeTab, selectedAgentId]);

  // Infinite scroll handler with throttling
  useEffect(() => {
    const listContainer = interactionsListRef.current;
    if (!listContainer || activeTab !== 'interactions') return;

    let scrollTimeout: NodeJS.Timeout | null = null;
    
    const handleScroll = () => {
      // Throttle scroll events
      if (scrollTimeout) return;
      
      scrollTimeout = setTimeout(() => {
        scrollTimeout = null;
        const { scrollTop, scrollHeight, clientHeight } = listContainer;
        // Load more when user scrolls to within 300px of the bottom
        if (scrollHeight - scrollTop - clientHeight < 300 && !isLoadingMoreRef.current) {
          loadMoreInteractions();
        }
      }, 100);
    };

    listContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      listContainer.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [activeTab, loadMoreInteractions]);

  if (!hasAdminAccess()) {
    return (
      <div className="agent-dashboard-container">
        <div className="access-denied">
          <h2>🔒 Admin Access Required</h2>
          <p>You don't have permission to access this agent dashboard.</p>
          <button className="back-button" onClick={handleGoHome} title="Go to Home">
            <Home size={16} style={{ marginRight: '4px' }} />
            Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="agent-dashboard-container">
      <div className="agent-dashboard-header">
        <div className="header-inline-content">
          <button className="back-button" onClick={handleGoHome} title="Go to Home">
            <Home size={16} style={{ marginRight: '4px' }} />
            Home
          </button>
          
          <div className="dashboard-tabs">
            <button 
              className={`dashboard-tab ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              <BarChart3 className="h-4 w-4 inline mr-1" />
              Analytics
            </button>
            
            <button 
              className={`dashboard-tab ${activeTab === 'interactions' ? 'active' : ''}`}
              onClick={() => setActiveTab('interactions')}
            >
              <MessageCircle className="h-4 w-4 inline mr-1" />
              Interactions
            </button>

            {!isAggregateDashboard && (
              <button
                className={`dashboard-tab ${activeTab === 'whats-new' ? 'active' : ''}`}
                onClick={() => setActiveTab('whats-new')}
              >
                <Star className="h-4 w-4 inline mr-1" />
                What's New
              </button>
            )}
            
            {agentConfig.hasTestResults && (
              <>
                <button 
                  className={`dashboard-tab ${activeTab === 'test-review' ? 'active' : ''}`}
                  onClick={() => setActiveTab('test-review')}
                >
                  <Search className="h-4 w-4 inline mr-1" />
                  Test Review
                </button>
                <button 
                  className={`dashboard-tab ${activeTab === 'testing' ? 'active' : ''}`}
                  onClick={() => setActiveTab('testing')}
                >
                  <FlaskConical className="h-4 w-4 inline mr-1" />
                  Test Results
                </button>
              </>
            )}
            
            {isSuperAdmin && (
              <button 
                className={`dashboard-tab ${activeTab === 'indexing' ? 'active' : ''}`}
                onClick={() => setActiveTab('indexing')}
              >
                <Database className="h-4 w-4 inline mr-1" />
                Management
              </button>
            )}
          </div>
          
          {/* Consistent title section with avatar on right for all tabs */}
          <div className="agent-title-section">
            <div className="agent-icon" style={{ color: agentConfig.color }}>
              {agentConfig.icon.startsWith('/') ? (
                <img src={agentConfig.icon} alt={agentConfig.displayName} className="agent-dashboard-logo" />
              ) : (
                <span className="agent-emoji">{agentConfig.icon}</span>
              )}
            </div>
            <div className="agent-title-text">
              <h1>{agentConfig.displayName} Dashboard</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        {activeTab === 'whats-new' && !isAggregateDashboard && (() => {
          const currentAgent = allAgents.find(a => a.id === agentConfig.id);
          return (
            <AgentWhatsNewEditor
              agentId={agentConfig.id}
              agentName={agentConfig.displayName}
              initialNotice={currentAgent?.updateNotice}
              version={currentAgent?.version}
            />
          );
        })()}

        {activeTab === 'interactions' && (
          <div className="interactions-section interactions-modern" ref={interactionsListRef}>
            {/* KPI Cards - matching analytics style */}
            <div className="interactions-kpi-grid">
              <div className="interactions-kpi-card">
                <div className="interactions-kpi-icon">
                  <MessageCircle size={24} />
                </div>
                <div className="interactions-kpi-content">
                  <span className="interactions-kpi-value">
                    {interactionStats.total}
                    {prevInteractionStats && <PctBadge current={interactionStats.total} previous={prevInteractionStats.total} />}
                  </span>
                  <span className="interactions-kpi-label">Total Conversations</span>
                </div>
              </div>
              
              <div className="interactions-kpi-card interactions-kpi-positive">
                <div className="interactions-kpi-icon">
                  <ThumbsUp size={24} />
                </div>
                <div className="interactions-kpi-content">
                  <span className="interactions-kpi-value">
                    {interactionStats.positive}
                    {prevInteractionStats && <PctBadge current={interactionStats.positive} previous={prevInteractionStats.positive} />}
                  </span>
                  <span className="interactions-kpi-label">Positive Feedback</span>
                </div>
              </div>
              
              <div className="interactions-kpi-card interactions-kpi-negative">
                <div className="interactions-kpi-icon">
                  <ThumbsDown size={24} />
                </div>
                <div className="interactions-kpi-content">
                  <span className="interactions-kpi-value">
                    {interactionStats.negative}
                    {prevInteractionStats && <PctBadge current={interactionStats.negative} previous={prevInteractionStats.negative} />}
                  </span>
                  <span className="interactions-kpi-label">Negative Feedback</span>
                </div>
              </div>
              
              <div className="interactions-kpi-card">
                <div className="interactions-kpi-icon">
                  <MessageSquare size={24} />
                </div>
                <div className="interactions-kpi-content">
                  <span className="interactions-kpi-value">{interactionStats.noFeedback}</span>
                  <span className="interactions-kpi-label">No Feedback</span>
                </div>
              </div>
              
              <div className="interactions-kpi-card interactions-kpi-rate">
                <div className="interactions-kpi-icon">
                  <Activity size={24} />
                </div>
                <div className="interactions-kpi-content">
                  <span className="interactions-kpi-value">
                    {interactionStats.positiveRate}%
                    {prevInteractionStats && <PctBadge current={interactionStats.positiveRate} previous={prevInteractionStats.positiveRate} mode="points" />}
                  </span>
                  <span className="interactions-kpi-label">Approval Rate</span>
                </div>
              </div>
            </div>
            
            {/* Controls Bar */}
            <div className="interactions-controls">
              <div className="interactions-controls-left">
                <button 
                  className="interactions-btn interactions-btn-primary"
                  onClick={async () => {
                    try {
                      const headers = await getAuthHeaders();
                      const params = new URLSearchParams({
                        timeWindow: interactionsTimeWindow,
                        feedbackFilter: interactionsFeedbackFilter,
                      });
                      const effectiveAgentId = isAggregateDashboard
                        ? (selectedAgentId === 'all' ? null : selectedAgentId)
                        : agentConfig.id;
                      if (effectiveAgentId) {
                        params.append('agentId', effectiveAgentId);
                      }
                      const exportStartDate = getStartDateFromTimeWindow(interactionsTimeWindow);
                      if (exportStartDate) {
                        params.append('startDate', exportStartDate);
                      }
                      if (interactionsSearchTerm) {
                        params.append('search', interactionsSearchTerm);
                      }
                      if (interactionsJobCode) {
                        params.append('jobCode', interactionsJobCode);
                      }
                      const response = await fetch(`${API_BASE_URL}/conversation-history/export?${params}`, {
                        method: 'GET',
                        headers
                      });
                      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
                      const blob = await response.blob();
                      const filenameAgent = effectiveAgentId || 'all';
                      const feedbackSuffix = interactionsFeedbackFilter !== 'all' ? `_${interactionsFeedbackFilter}` : '';
                      const filename = `${filenameAgent}_Interactions_${interactionsTimeWindow}${feedbackSuffix}_${new Date().toISOString().slice(0, 10)}.csv`;
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch (error) {
                      console.error('Export failed:', error);
                      alert('Failed to export. Please try again.');
                    }
                  }}
                  disabled={loadingInteractions}
                >
                  <Download size={16} />
                  Export CSV
                </button>
                
                <button 
                  className="interactions-btn interactions-btn-secondary" 
                  onClick={() => loadInteractions(false)} 
                  disabled={loadingInteractions}
                >
                  <RefreshCw size={16} className={loadingInteractions ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>

              <div className="interactions-controls-right">
                {isAggregateDashboard && (
                  <div className="interactions-filter">
                    <label>Agent</label>
                    <select
                      value={selectedAgentId}
                      onChange={(e) => setSelectedAgentId(e.target.value)}
                    >
                      <option value="all">All agents</option>
                      {agentFilterOptions.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="interactions-filter">
                  <label>Time</label>
                  <select 
                    value={interactionsTimeWindow}
                    onChange={(e) => setInteractionsTimeWindow(e.target.value as TimeWindow)}
                  >
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="all">All Time</option>
                  </select>
                </div>
                
                <div className="interactions-filter">
                  <label>Feedback</label>
                  <select 
                    value={interactionsFeedbackFilter}
                    onChange={(e) => setInteractionsFeedbackFilter(e.target.value as any)}
                  >
                    <option value="all">All</option>
                    <option value="positive">Positive</option>
                    <option value="negative">Negative</option>
                    <option value="none">None</option>
                  </select>
                </div>

                {availableJobCodes.length > 0 && (
                  <div className="interactions-filter">
                    <label>Job Code</label>
                    <select
                      value={interactionsJobCode}
                      onChange={(e) => setInteractionsJobCode(e.target.value)}
                    >
                      <option value="">All</option>
                      {availableJobCodes.map(jc => (
                        <option key={jc} value={jc}>{jc}</option>
                      ))}
                    </select>
                  </div>
                )}
                
              </div>
            </div>

            {/* Search Bar */}
            <div className="interactions-search">
              <Search size={18} className="interactions-search-icon" />
              <input
                type="text"
                placeholder="Search conversation content..."
                value={interactionsSearchTerm}
                onChange={(e) => setInteractionsSearchTerm(e.target.value)}
                className="interactions-search-input"
              />
              {interactionsSearchTerm && (
                <button
                  onClick={() => setInteractionsSearchTerm('')}
                  className="interactions-search-clear"
                >
                  Clear
                </button>
              )}
            </div>
            
            {loadingInteractions && (
              <div className="loading-state">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p>Loading interactions...</p>
              </div>
            )}
            
            {interactionsError && (
              <div className="error-state">
                <AlertTriangle className="h-6 w-6" />
                <p>{interactionsError}</p>
              </div>
            )}
            
            {!loadingInteractions && !interactionsError && interactions.length === 0 && (
              <div className="empty-state">
                <MessageCircle className="h-12 w-12 opacity-50" />
                <p>No interactions found</p>
              </div>
            )}
            
            {!loadingInteractions && !interactionsError && interactions.length > 0 && (
              <div className="interactions-list-modern">
                <div className="interactions-count">
                  Showing {interactions.length}{hasMoreInteractions ? '+' : ''} of {interactionStats.total} interactions
                </div>
                
                {interactions.map((interaction) => {
                  const isExpanded = expandedInteractionIds.has(interaction.id);
                  const updatedDate = new Date(interaction.updatedAt);
                  
                  return (
                    <div 
                      key={interaction.id} 
                      className={`interaction-card-modern ${isExpanded ? 'expanded' : ''}`}
                    >
                      <div 
                        className="interaction-card-header"
                        onClick={() => {
                          const newExpanded = new Set(expandedInteractionIds);
                          if (isExpanded) {
                            newExpanded.delete(interaction.id);
                          } else {
                            newExpanded.add(interaction.id);
                            // Lazy load messages if not already loaded (for optimized table-based responses)
                            if (interaction.messages === null && !loadingMessagesIds.has(interaction.id)) {
                              loadMessagesForInteraction(interaction.id, interaction.userId, interaction.agentId);
                            }
                          }
                          setExpandedInteractionIds(newExpanded);
                        }}
                      >
                        <div className="interaction-card-info">
                          <div className="interaction-card-meta">
                            {/* Feedback indicator */}
                            {interaction.feedback ? (
                              <span className={`interaction-feedback-badge ${interaction.feedback.type}`}>
                                {interaction.feedback.type === 'positive' ? <ThumbsUp size={14} /> : <ThumbsDown size={14} />}
                              </span>
                            ) : (
                              <span className="interaction-feedback-badge none">—</span>
                            )}
                            
                            <span className="interaction-date">
                              {updatedDate.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                            
                            <span className="interaction-user">
                              <User size={12} />
                              {interaction.userId}
                            </span>
                            
                            <span className="interaction-messages-count">
                              <MessageCircle size={12} />
                              {interaction.messageCount} messages
                            </span>
                          </div>
                          
                          {/* Product styles (if Product agent) */}
                          {interaction.hasProductData && interaction.products?.styles && interaction.products.styles.length > 0 && (
                            <div className="interaction-product-styles">
                              <Package size={16} style={{ color: '#10b981' }} />
                              {interaction.products.styles.slice(0, 8).map((style, i) => (
                                <span key={i} className="interaction-style-tag">
                                  {style}
                                </span>
                              ))}
                              {interaction.products.styles.length > 8 && (
                                <span className="interaction-more-styles">
                                  +{interaction.products.styles.length - 8} more
                                </span>
                              )}
                            </div>
                          )}
                          
                          <div className="interaction-preview">
                            {interaction.title}
                          </div>
                          
                          {/* Feedback notes/tags if present */}
                          {interaction.feedback && (interaction.feedback.notes || interaction.feedback.tags?.length > 0) && (
                            <div className="interaction-feedback-tags">
                              {interaction.feedback.tags?.map((tag, i) => (
                                <span key={i} className="interaction-tag">{tag}</span>
                              ))}
                              {interaction.feedback.notes && (
                                <span className="interaction-notes">
                                  "{interaction.feedback.notes.substring(0, 50)}{interaction.feedback.notes.length > 50 ? '...' : ''}"
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="interaction-expand-icon">▶</div>
                      </div>
                      
                      {isExpanded && (
                        <div style={{ borderTop: '1px solid #e3e5e8', backgroundColor: '#fafafa' }}>
                          {/* Product metadata section */}
                          {interaction.hasProductData && interaction.products && (
                            <div style={{ padding: '1rem', backgroundColor: '#f0fdf4', borderBottom: '1px solid #dcfce7' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontWeight: '600', color: '#047857' }}>
                                <Package className="h-4 w-4" />
                                Products Displayed ({interaction.products.count})
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                {interaction.products.styles?.length > 0 && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
                                    <span style={{ fontWeight: '500', color: '#3d4045', marginRight: '0.5rem' }}>Style Codes:</span>
                                    {interaction.products.styles.map((style, i) => (
                                      <span key={i} style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                        {style}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {interaction.products.names?.length > 0 && (
                                  <div>
                                    <span style={{ fontWeight: '500', color: '#3d4045' }}>Product Names:</span>
                                    <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, listStyle: 'disc' }}>
                                      {interaction.products.names.slice(0, 10).map((name, i) => (
                                        <li key={i} style={{ fontSize: '0.8rem', color: '#53575d' }}>{name}</li>
                                      ))}
                                      {interaction.products.names.length > 10 && (
                                        <li style={{ fontSize: '0.8rem', color: '#6f747b', fontStyle: 'italic' }}>+{interaction.products.names.length - 10} more...</li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Metadata */}
                          <div style={{ padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', backgroundColor: '#f8f9fa' }}>
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase' }}>Conversation ID</span>
                              <div style={{ fontSize: '0.75rem', color: '#1f2124', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={interaction.id}>{interaction.id}</div>
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase' }}>User</span>
                              <div style={{ fontSize: '0.875rem', color: '#1f2124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={interaction.userId}>{interaction.userId}</div>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase' }}>Created</span>
                              <div style={{ fontSize: '0.875rem', color: '#1f2124' }}>{new Date(interaction.createdAt).toLocaleString()}</div>
                            </div>
                          </div>
                          
                          {/* Feedback details section - shows which message feedback was on */}
                          {interaction.feedback && (
                            <div style={{ padding: '1rem', backgroundColor: interaction.feedback.type === 'positive' ? '#f0fdf4' : '#fef2f2', borderTop: '1px solid', borderColor: interaction.feedback.type === 'positive' ? '#dcfce7' : '#fecaca' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                {interaction.feedback.type === 'positive' ? <ThumbsUp className="h-4 w-4" style={{ color: '#10b981' }} /> : <ThumbsDown className="h-4 w-4" style={{ color: '#ef4444' }} />}
                                <span style={{ fontWeight: '600', color: interaction.feedback.type === 'positive' ? '#047857' : '#b91c1c' }}>
                                  {interaction.feedback.type === 'positive' ? 'Positive Feedback' : 'Negative Feedback'}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#6f747b' }}>
                                  ({new Date(interaction.feedback.created_at).toLocaleString()})
                                </span>
                              </div>
                              
                              {/* Show feedback tags */}
                              {interaction.feedback?.tags?.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                                  {interaction.feedback.tags.map((tag: string, i: number) => (
                                    <span key={i} style={{ backgroundColor: interaction.feedback?.type === 'positive' ? '#dcfce7' : '#fecaca', color: interaction.feedback?.type === 'positive' ? '#166534' : '#991b1b', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem' }}>
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              {/* Show feedback notes */}
                              {interaction.feedback.notes && (
                                <div style={{ backgroundColor: '#fff', border: '1px solid #e3e5e8', borderRadius: '0.375rem', padding: '0.75rem', marginTop: '0.5rem' }}>
                                  <span style={{ fontSize: '0.75rem', color: '#6f747b', display: 'block', marginBottom: '0.25rem' }}>User Notes:</span>
                                  <p style={{ fontSize: '0.875rem', color: '#3d4045', margin: 0, whiteSpace: 'pre-wrap' }}>{interaction.feedback.notes}</p>
                                </div>
                              )}
                              
                              {/* Show which message feedback was on */}
                              <div style={{ marginTop: '0.75rem', padding: '0.5rem', backgroundColor: '#fff', border: '1px solid #e3e5e8', borderRadius: '0.375rem' }}>
                                <span style={{ fontSize: '0.75rem', color: '#6f747b', display: 'block', marginBottom: '0.25rem' }}>Feedback was on this exchange:</span>
                                <div style={{ fontSize: '0.8rem', color: '#1e40af', marginBottom: '0.25rem' }}>
                                  <strong>User:</strong> {interaction.lastUserMessage?.substring(0, 150)}{interaction.lastUserMessage?.length > 150 ? '...' : ''}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#047857' }}>
                                  <strong>Agent:</strong> {interaction.lastAgentMessage?.substring(0, 150)}{interaction.lastAgentMessage?.length > 150 ? '...' : ''}
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* Messages */}
                          <div style={{ padding: '1rem' }}>
                            <h4 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#1f2124', marginBottom: '1rem', textTransform: 'uppercase' }}>
                              Conversation History ({interaction.messageCount || interaction.messages?.length || 0} messages)
                            </h4>
                            {loadingMessagesIds.has(interaction.id) ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6f747b', padding: '1rem' }}>
                                <div className="animate-spin" style={{ width: '1rem', height: '1rem', border: '2px solid #e3e5e8', borderTopColor: '#3b82f6', borderRadius: '50%' }}></div>
                                Loading messages...
                              </div>
                            ) : !interaction.messages || interaction.messages.length === 0 ? (
                              <div style={{ color: '#9da2a8', fontStyle: 'italic', padding: '0.5rem' }}>
                                {interaction.messages === null ? 'Click to load conversation history' : 'No messages found'}
                              </div>
                            ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {interaction.messages.map((msg: any, idx: number) => {
                                  // Check if this message or the following agent message has feedback
                                  // Feedback is matched on user message, so check if this user message matches
                                  const isFeedbackMessage = interaction.feedback && msg.sender === 'user' && 
                                    msg.content?.toLowerCase().trim().substring(0, 100) === interaction.lastUserMessage?.toLowerCase().trim().substring(0, 100);
                                  const isAgentAfterFeedback = interaction.feedback && msg.sender === 'agent' && idx > 0 &&
                                    interaction.messages?.[idx - 1]?.content?.toLowerCase().trim().substring(0, 100) === interaction.lastUserMessage?.toLowerCase().trim().substring(0, 100);
                                  const hasFeedbackHighlight = isFeedbackMessage || isAgentAfterFeedback;
                                  
                                  return (
                                  <div 
                                    key={msg.id || idx}
                                    style={{
                                      backgroundColor: msg.sender === 'user' ? '#eff6ff' : msg.sender === 'agent' ? '#f0fdf4' : '#f8f9fa',
                                      border: msg.sender === 'user' ? '1px solid #dbeafe' : msg.sender === 'agent' ? '1px solid #dcfce7' : '1px solid #e3e5e8',
                                      borderRadius: '0.5rem',
                                      padding: '0.75rem',
                                      borderLeft: msg.sender === 'user' ? '4px solid #3b82f6' : msg.sender === 'agent' ? '4px solid #10b981' : '4px solid #9da2a8',
                                      position: 'relative',
                                      boxShadow: hasFeedbackHighlight ? `0 0 0 2px ${interaction.feedback?.type === 'positive' ? '#10b981' : '#ef4444'}` : undefined
                                    }}
                                  >
                                    {/* Feedback badge on the message that was rated */}
                                    {isAgentAfterFeedback && interaction.feedback && (
                                      <div style={{ 
                                        position: 'absolute', 
                                        top: '-0.5rem', 
                                        right: '0.5rem', 
                                        backgroundColor: interaction.feedback.type === 'positive' ? '#10b981' : '#ef4444', 
                                        color: '#fff', 
                                        padding: '0.125rem 0.5rem', 
                                        borderRadius: '0.25rem', 
                                        fontSize: '0.65rem',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem'
                                      }}>
                                        {interaction.feedback.type === 'positive' ? <ThumbsUp className="h-3 w-3" /> : <ThumbsDown className="h-3 w-3" />}
                                        Feedback Given
                                      </div>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                      <strong style={{ color: msg.sender === 'user' ? '#1e40af' : msg.sender === 'agent' ? '#047857' : '#53575d' }}>
                                        {msg.sender === 'user' ? <><User className="h-4 w-4" style={{ display: 'inline', marginRight: '0.25rem' }} /> User</> : 
                                         msg.sender === 'agent' ? <><Bot className="h-4 w-4" style={{ display: 'inline', marginRight: '0.25rem' }} /> {msg.agentName || 'Agent'}</> : 
                                         'System'}
                                      </strong>
                                      <span style={{ fontSize: '0.75rem', color: '#6f747b' }}>{new Date(msg.timestamp).toLocaleString()}</span>
                                    </div>
                                    <div style={{ fontSize: '0.875rem', color: '#3d4045', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Load more indicator */}
                {loadingMoreInteractions && (
                  <div className="interactions-loading-more">
                    <Loader2 size={20} className="animate-spin" />
                    <span>Loading more...</span>
                  </div>
                )}
                
                {!loadingMoreInteractions && hasMoreInteractions && interactions.length >= INTERACTIONS_PAGE_SIZE && (
                  <div className="interactions-load-more-trigger" style={{ height: '1px' }} />
                )}
                
                {!hasMoreInteractions && interactions.length > 0 && (
                  <div className="interactions-end-message">
                    All {interactions.length} interactions loaded
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Logs and Feedback tabs removed - functionality merged into Interactions */}
        {false && (
          <div className="logs-section">
            <div className="section-header">
              <div className="section-header-buttons">
                <button 
                  className="export-button"
                  onClick={async () => {
                    try {
                      const headers = await getAuthHeaders();
                      
                      // Build export URL with current filters
                      const params = new URLSearchParams({
                        agentId: agentConfig.id,
                        timeWindow: logsTimeWindow
                      });
                      
                      const response = await fetch(`${API_BASE_URL}/conversation-history/export?${params}`, {
                        method: 'GET',
                        headers
                      });

                      if (!response.ok) {
                        throw new Error(`Export failed: ${response.status}`);
                      }

                      const blob = await response.blob();
                      const filename = `${agentConfig.id}_Logs_${logsTimeWindow}_${new Date().toISOString().slice(0, 10)}.csv`;
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);

                      console.log('✅ Logs exported successfully');
                    } catch (error) {
                      console.error('❌ Export failed:', error);
                      alert('Failed to export logs. Please try again.');
                    }
                  }}
                  disabled={loadingLogs}
                >
                  <Download className="h-4 w-4 inline mr-1" />
                  Export CSV
                </button>
                
                <button 
                  className="refresh-button"
                  onClick={loadLogs}
                  disabled={loadingLogs}
                >
                  <RefreshCw className={`h-4 w-4 ${loadingLogs ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                
                <div className="logs-limit-selector">
                  <label htmlFor="logs-time-window">Time: </label>
                  <select 
                    id="logs-time-window" 
                    value={logsTimeWindow}
                    onChange={(e) => setLogsTimeWindow(e.target.value as TimeWindow)}
                    style={{ marginRight: '1rem' }}
                  >
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="6months">Last 6 Months</option>
                    <option value="all">All Time</option>
                  </select>
                  
                  <label htmlFor="logs-limit">Show: </label>
                  <select 
                    id="logs-limit" 
                    value={logsLimit}
                    onChange={(e) => setLogsLimit(Number(e.target.value))}
                  >
                    <option value={50}>Most Recent 50</option>
                    <option value={100}>Most Recent 100</option>
                    <option value={500}>Most Recent 500</option>
                    <option value={0}>All Interactions</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Search Bar */}
            <div className="search-bar-container" style={{ padding: '1rem', borderBottom: '1px solid #e3e5e8' }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <div style={{ flex: 1, position: 'relative' }}>
                  <Search className="h-4 w-4" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#9da2a8' }} />
                  <input
                    type="text"
                    placeholder="Search conversation content..."
                    value={logsSearchTerm}
                    onChange={(e) => setLogsSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '2.5rem',
                      paddingRight: '1rem',
                      paddingTop: '0.5rem',
                      paddingBottom: '0.5rem',
                      border: '1px solid #cfd2d6',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label htmlFor="search-type" style={{ fontSize: '0.875rem', color: '#6f747b', whiteSpace: 'nowrap' }}>
                    Search in:
                  </label>
                  <select
                    id="search-type"
                    value={logsSearchType}
                    onChange={(e) => setLogsSearchType(e.target.value as 'all' | 'user' | 'agent')}
                    style={{
                      padding: '0.5rem',
                      border: '1px solid #cfd2d6',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="all">All Messages</option>
                    <option value="user">User Messages Only</option>
                    <option value="agent">Agent Messages Only</option>
                  </select>
                </div>
                {logsSearchTerm && (
                  <button
                    onClick={() => setLogsSearchTerm('')}
                    style={{
                      padding: '0.5rem',
                      color: '#6f747b',
                      fontSize: '0.875rem',
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
              {logsSearchTerm && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6f747b' }}>
                  Showing {logEntries.length} of {totalLogsUnfiltered} conversations
                </div>
              )}
            </div>
            
            {loadingLogs && (
              <div className="loading-state">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p>Loading interaction logs...</p>
              </div>
            )}
            
            {logsError && (
              <div className="error-state">
                <AlertTriangle className="h-6 w-6" />
                <p>{logsError}</p>
              </div>
            )}
            
            {!loadingLogs && !logsError && logEntries.length === 0 && (
              <div className="empty-state">
                <FileText className="h-12 w-12 opacity-50" />
                <p>No interaction logs found</p>
              </div>
            )}
            
            {!loadingLogs && !logsError && logEntries.length > 0 && (
              <div className="logs-list">
                <div className="logs-header">
                  <span className="log-count">{logEntries.length} interactions</span>
                </div>
                
                {logEntries.map((log) => {
                  const isExpanded = expandedLogIds.has(log.id);
                  const createdDate = new Date(log.createdAt);
                  const updatedDate = new Date(log.updatedAt);
                  
                  return (
                    <div 
                      key={log.id} 
                      style={{
                        backgroundColor: 'white',
                        border: '1px solid #e3e5e8',
                        borderRadius: '0.5rem',
                        marginBottom: '0.75rem',
                        overflow: 'hidden',
                        transition: 'box-shadow 0.2s',
                        boxShadow: isExpanded ? '0 4px 6px -1px rgba(0, 0, 0, 0.1)' : 'none'
                      }}
                    >
                      <div 
                        onClick={() => {
                          const newExpanded = new Set(expandedLogIds);
                          if (isExpanded) {
                            newExpanded.delete(log.id);
                          } else {
                            newExpanded.add(log.id);
                          }
                          setExpandedLogIds(newExpanded);
                        }}
                        style={{
                          padding: '1rem',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: isExpanded ? '#f8f9fa' : 'white',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!isExpanded) e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          if (!isExpanded) e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                            <span style={{ 
                              fontSize: '0.875rem', 
                              color: '#6f747b', 
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}>
                              {updatedDate.toLocaleString('en-US', { 
                                month: 'short', 
                                day: 'numeric', 
                                year: 'numeric',
                                hour: '2-digit', 
                                minute: '2-digit'
                              })}
                            </span>
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '0.25rem',
                              fontSize: '0.875rem',
                              color: '#53575d',
                              backgroundColor: '#f1f2f4',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem'
                            }}>
                              <User className="h-3 w-3" />
                              {log.userId}
                            </span>
                            <span style={{ 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '0.25rem',
                              fontSize: '0.875rem',
                              color: '#059669',
                              backgroundColor: '#d1fae5',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontWeight: '500'
                            }}>
                              <MessageCircle className="h-3 w-3" />
                              {log.messageCount} messages
                            </span>
                          </div>
                          <div style={{ 
                            fontSize: '0.875rem', 
                            color: '#1f2124',
                            fontWeight: '400',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {log.title}
                          </div>
                        </div>
                        <div style={{ 
                          marginLeft: '1rem', 
                          color: '#9da2a8',
                          fontSize: '1.25rem',
                          transition: 'transform 0.2s',
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                        }}>
                          ▶
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div style={{ 
                          borderTop: '1px solid #e3e5e8',
                          backgroundColor: '#fafafa'
                        }}>
                          {/* Metadata Section */}
                          <div style={{ 
                            padding: '1rem',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '0.75rem',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                Conversation ID
                              </span>
                              <span style={{ fontSize: '0.875rem', color: '#1f2124', fontFamily: 'monospace' }}>
                                {log.id}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                User ID
                              </span>
                              <span style={{ fontSize: '0.875rem', color: '#1f2124' }}>
                                {log.userId}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                Agent
                              </span>
                              <span style={{ fontSize: '0.875rem', color: '#1f2124' }}>
                                {log.agentName || log.agentId}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                Created
                              </span>
                              <span style={{ fontSize: '0.875rem', color: '#1f2124' }}>
                                {createdDate.toLocaleString()}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                Last Updated
                              </span>
                              <span style={{ fontSize: '0.875rem', color: '#1f2124' }}>
                                {updatedDate.toLocaleString()}
                              </span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontSize: '0.75rem', color: '#6f747b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                                Total Messages
                              </span>
                              <span style={{ fontSize: '0.875rem', color: '#1f2124', fontWeight: '600' }}>
                                {log.messageCount}
                              </span>
                            </div>
                          </div>
                          
                          {/* Conversation History */}
                          {log.messages && log.messages.length > 0 && (
                            <div style={{ padding: '1rem' }}>
                              <h4 style={{ 
                                fontSize: '0.875rem', 
                                fontWeight: '600', 
                                color: '#1f2124', 
                                marginBottom: '1rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                              }}>
                                Full Conversation History
                              </h4>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {log.messages.map((msg: any, idx: number) => (
                                  <div 
                                    key={msg.id || idx}
                                    style={{
                                      backgroundColor: msg.sender === 'user' ? '#eff6ff' : msg.sender === 'agent' ? '#f0fdf4' : '#f8f9fa',
                                      border: msg.sender === 'user' ? '1px solid #dbeafe' : msg.sender === 'agent' ? '1px solid #dcfce7' : '1px solid #e3e5e8',
                                      borderRadius: '0.5rem',
                                      padding: '0.75rem',
                                      borderLeft: msg.sender === 'user' ? '4px solid #3b82f6' : msg.sender === 'agent' ? '4px solid #10b981' : '4px solid #9da2a8'
                                    }}
                                  >
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      marginBottom: '0.5rem'
                                    }}>
                                      <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '0.5rem',
                                        fontSize: '0.875rem',
                                        fontWeight: '600',
                                        color: msg.sender === 'user' ? '#1e40af' : msg.sender === 'agent' ? '#047857' : '#53575d'
                                      }}>
                                        {msg.sender === 'user' ? (
                                          <><User className="h-4 w-4" /> User</>
                                        ) : msg.sender === 'agent' ? (
                                          <><Bot className="h-4 w-4" /> {msg.agentName || 'Agent'}</>
                                        ) : (
                                          <><MessageCircle className="h-4 w-4" /> System</>
                                        )}
                                      </div>
                                      <div style={{ fontSize: '0.75rem', color: '#6f747b' }}>
                                        {new Date(msg.timestamp).toLocaleString()}
                                      </div>
                                    </div>
                                                    <div style={{ 
                                                      fontSize: '0.875rem', 
                                                      color: '#3d4045',
                                                      lineHeight: '1.5',
                                                      whiteSpace: 'pre-wrap'
                                                    }}>
                                                      {msg.content}
                                                    </div>
                                    
                                                    {/* Display product info if available */}
                                                    {msg.metadata?.products && (
                                                      <div className="conv-message-metadata" style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.375rem', border: '1px solid #dcfce7' }}>
                                                        <div className="metadata-header" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: '600', color: '#047857' }}>
                                                          <Package className="h-4 w-4" />
                                                          Products Displayed ({msg.metadata.products.count || msg.metadata.products.styles?.length || 0})
                                                        </div>
                                                        <div className="metadata-details" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                          {/* Style Codes */}
                                                          {msg.metadata.products.styles && msg.metadata.products.styles.length > 0 && (
                                                            <div className="metadata-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                                              <span className="metadata-key" style={{ fontWeight: '500', color: '#3d4045', marginRight: '0.5rem' }}>Style Codes:</span>
                                                              {msg.metadata.products.styles.map((style: string, i: number) => (
                                                                <span key={i} style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                                                  {style}
                                                                </span>
                                                              ))}
                                                            </div>
                                                          )}
                                                          {/* Product Names */}
                                                          {msg.metadata.products.names && msg.metadata.products.names.length > 0 && (
                                                            <div className="metadata-row">
                                                              <span className="metadata-key" style={{ fontWeight: '500', color: '#3d4045' }}>Product Names:</span>
                                                              <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, listStyle: 'disc' }}>
                                                                {msg.metadata.products.names.slice(0, 10).map((name: string, i: number) => (
                                                                  <li key={i} style={{ fontSize: '0.8rem', color: '#53575d' }}>{name}</li>
                                                                ))}
                                                                {msg.metadata.products.names.length > 10 && (
                                                                  <li style={{ fontSize: '0.8rem', color: '#6f747b', fontStyle: 'italic' }}>
                                                                    +{msg.metadata.products.names.length - 10} more...
                                                                  </li>
                                                                )}
                                                              </ul>
                                                            </div>
                                                          )}
                                                          {/* SKUs if available */}
                                                          {msg.metadata.products.skus && msg.metadata.products.skus.length > 0 && (
                                                            <div className="metadata-row" style={{ fontSize: '0.75rem', color: '#6f747b' }}>
                                                              SKUs: {msg.metadata.products.skus.slice(0, 5).join(', ')}{msg.metadata.products.skus.length > 5 ? ` +${msg.metadata.products.skus.length - 5} more` : ''}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    )}
                                                    
                                                    {/* Display other metadata if available */}
                                                    {msg.metadata && Object.keys(msg.metadata).filter(k => k !== 'products').length > 0 && (
                                                      <div className="conv-message-metadata">
                                                        <div className="metadata-header">
                                                          <Wrench className="h-3 w-3 inline mr-1" />
                                                          Other Metadata:
                                                        </div>
                                                        <div className="metadata-details">
                                                          {Object.keys(msg.metadata).filter(k => k !== 'products').map(key => (
                                                            <div key={key} className="metadata-row">
                                                              <span className="metadata-key">{key}:</span>
                                                              <span className="metadata-val">{JSON.stringify(msg.metadata[key]).substring(0, 100)}</span>
                                                            </div>
                                                          ))}
                                                        </div>
                                                      </div>
                                                    )}
                                    
                                    {/* Display tool calls if available */}
                                    {msg.toolCalls && msg.toolCalls.length > 0 && (
                                      <div className="conv-tool-calls">
                                        <div className="tool-calls-header">
                                          <Wrench className="h-3 w-3 inline mr-1" />
                                          Tool Calls ({msg.toolCalls.length}):
                                        </div>
                                        <div className="tool-calls-list">
                                          {msg.toolCalls.map((tool: string, toolIdx: number) => (
                                            <span key={toolIdx} className="tool-call-badge">{tool}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        
        {false && (
          <div className="feedback-section">
            <div className="section-header">
              <div className="section-header-buttons">
                <button 
                  className="export-button"
                  onClick={async () => {
                    try {
                      const headers = await getAuthHeaders();
                      headers['Accept'] = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                      delete headers['Content-Type'];

                      const response = await fetch(`${API_BASE_URL}/feedback/export?agent=${agentConfig.id}`, {
                        method: 'GET',
                        headers
                      });

                      if (!response.ok) {
                        const errorText = await response.text();
                        console.error('Export failed with status:', response.status, errorText);
                        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
                      }

                      const contentDisposition = response.headers.get('Content-Disposition');
                      const filename = contentDisposition 
                        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
                        : `${agentConfig.id}_Feedback_${new Date().toISOString().slice(0, 10)}.xlsx`;

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = filename;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);

                      console.log('✅ Feedback exported successfully');
                    } catch (error) {
                      console.error('❌ Export failed:', error);
                      alert('Failed to export feedback. Please try again.');
                    }
                  }}
                  disabled={loadingFeedback}
                >
                  <Download className="h-4 w-4 inline mr-1" />
                  Export to Excel
                </button>
                <button 
                  className="refresh-button"
                  onClick={() => loadFeedback()}
                  disabled={loadingFeedback}
                >
                  {loadingFeedback ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 inline mr-1" />
                      Refresh
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {feedbackError && (
              <div className="error-message">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                {feedbackError}
              </div>
            )}
            
            {loadingFeedback ? (
              <div className="loading-state">
                <div className="loading-spinner"><Loader2 className="h-6 w-6 animate-spin" /></div>
                <p>Loading feedback data...</p>
              </div>
            ) : (
              <div className="feedback-content">
                {/* Feedback Statistics */}
                <div className="feedback-stats">
                  <div className="stat-card positive">
                    <div className="stat-number">{feedbackStats.positive}</div>
                    <div className="stat-label">Positive</div>
                    <div className="stat-percentage">
                      {feedbackStats.total > 0 ? Math.round((feedbackStats.positive / feedbackStats.total) * 100) : 0}%
                    </div>
                  </div>
                  <div className="stat-card negative">
                    <div className="stat-number">{feedbackStats.negative}</div>
                    <div className="stat-label">Negative</div>
                    <div className="stat-percentage">
                      {feedbackStats.total > 0 ? Math.round((feedbackStats.negative / feedbackStats.total) * 100) : 0}%
                    </div>
                  </div>
                  <div className="stat-card total">
                    <div className="stat-number">{feedbackStats.total}</div>
                    <div className="stat-label">Total</div>
                    <div className="stat-percentage">
                      {totalFeedbackCount > feedbackStats.total ? `of ${totalFeedbackCount}` : 'All'}
                    </div>
                  </div>
                </div>

                {/* Feedback Controls */}
                <div className="feedback-controls">
                  <div className="feedback-filters">
                    <select 
                      value={feedbackFilters.timeWindow} 
                      onChange={(e) => {
                        const newFilters = { ...feedbackFilters, timeWindow: e.target.value as TimeWindow };
                        setFeedbackFilters(newFilters);
                        loadFeedback(newFilters);
                      }}
                      className="feedback-sort-dropdown"
                      style={{ fontWeight: '500' }}
                    >
                      <option value="today">Today</option>
                      <option value="week">This Week</option>
                      <option value="month">This Month</option>
                      <option value="6months">Last 6 Months</option>
                      <option value="all">All Time</option>
                    </select>
                    
                    <select 
                      value={feedbackFilters.sortBy} 
                      onChange={(e) => {
                        const newFilters = { ...feedbackFilters, sortBy: e.target.value };
                        setFeedbackFilters(newFilters);
                        loadFeedback(newFilters);
                      }}
                      className="feedback-sort-dropdown"
                    >
                      <option value="created_at">Sort by Date</option>
                      <option value="feedback">Sort by Type</option>
                    </select>
                    
                    <select 
                      value={feedbackFilters.sortOrder} 
                      onChange={(e) => {
                        const newFilters = { ...feedbackFilters, sortOrder: e.target.value as 'asc' | 'desc' };
                        setFeedbackFilters(newFilters);
                        loadFeedback(newFilters);
                      }}
                      className="feedback-sort-dropdown"
                    >
                      <option value="desc">Newest First</option>
                      <option value="asc">Oldest First</option>
                    </select>

                    <select 
                      value={feedbackFilters.limit} 
                      onChange={(e) => {
                        const newFilters = { ...feedbackFilters, limit: parseInt(e.target.value) };
                        setFeedbackFilters(newFilters);
                        loadFeedback(newFilters);
                      }}
                      className="feedback-sort-dropdown"
                    >
                      <option value="25">Show 25</option>
                      <option value="50">Show 50</option>
                      <option value="100">Show 100</option>
                      <option value="500">Show 500</option>
                      <option value="10000">All Feedback</option>
                    </select>
                  </div>

                  <button 
                    onClick={() => loadFeedback()}
                    disabled={loadingFeedback}
                    className="feedback-refresh-button"
                  >
                    {loadingFeedback ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 inline mr-1" />
                        Refresh
                      </>
                    )}
                  </button>
                </div>

                {/* Feedback Entries */}
                {feedbackEntries.length > 0 ? (
                  <div className="feedback-entries">
                    <h4>Recent Feedback ({feedbackEntries.length} of {totalFeedbackCount})</h4>
                    <div className="feedback-list">
                      {feedbackEntries.map((entry) => {
                        const isExpanded = expandedFeedbackIds.has(entry.uid);
                        const isEnhanced = entry.data_version === 'v2';
                        
                        return (
                          <div key={entry.uid} className={`feedback-item ${entry.feedback}`}>
                            <div className="feedback-header">
                              <div className={`feedback-badge ${entry.feedback}`}>
                                {entry.feedback === 'positive' ? <ThumbsUp size={16} /> : <ThumbsDown size={16} />} {entry.feedback}
                                {isEnhanced && <span className="v2-badge" title="Enhanced feedback with full context">v2</span>}
                              </div>
                              <div className="feedback-meta">
                                <div className="feedback-date">
                                  {new Date(entry.created_at).toLocaleDateString()} at {new Date(entry.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                                {(entry.user_email || entry.user_id) && (
                                  <div className="feedback-user-email" title="User email">
                                    <Mail className="h-3 w-3 inline mr-1" />
                                    {entry.user_email || entry.user_id}
                                  </div>
                                )}
                                {entry.one_shot && (
                                  <div className="feedback-oneshot">One-shot</div>
                                )}
                                {isEnhanced && (
                                  <div className="enhanced-meta">
                                    {(entry.conversation_message_count ?? 0) > 0 && (
                                      <span className="meta-badge" title="Conversation messages captured">
                                        <MessageSquare className="h-3 w-3 inline mr-1" />
                                        {entry.conversation_message_count}
                                      </span>
                                    )}
                                    {(entry.tool_call_count ?? 0) > 0 && (
                                      <span className="meta-badge" title="Tool calls captured">
                                        <Wrench className="h-3 w-3 inline mr-1" />
                                        {entry.tool_call_count}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div className="feedback-content-item">
                              <div className="user-message scrollable-message">
                                <strong>User:</strong> {entry.user_message}
                              </div>
                              <div className="agent-message scrollable-message">
                                <strong>Agent:</strong> {entry.agent_message}
                              </div>
                              
                              {/* Display product metadata prominently */}
                              {entry.metadata && entry.metadata.products && (
                                <div className="feedback-metadata" style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#f0fdf4', borderRadius: '0.375rem', border: '1px solid #dcfce7' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontWeight: '600', color: '#047857' }}>
                                    <Package className="h-4 w-4" />
                                    Products Displayed ({entry.metadata.products.count || entry.metadata.products.styles?.length || 0})
                                  </div>
                                  <div className="metadata-section" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    {/* Style Codes - prominent display */}
                                    {entry.metadata.products.styles && entry.metadata.products.styles.length > 0 && (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', alignItems: 'center' }}>
                                        <span style={{ fontWeight: '500', color: '#3d4045', marginRight: '0.5rem' }}>Style Codes:</span>
                                        {entry.metadata.products.styles.map((style: string, i: number) => (
                                          <span key={i} style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.125rem 0.375rem', borderRadius: '0.25rem', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                            {style}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                    {/* Product Names - list format */}
                                    {entry.metadata.products.names && entry.metadata.products.names.length > 0 && (
                                      <div>
                                        <span style={{ fontWeight: '500', color: '#3d4045' }}>Product Names:</span>
                                        <ul style={{ margin: '0.25rem 0 0 1rem', padding: 0, listStyle: 'disc' }}>
                                          {entry.metadata.products.names.slice(0, 8).map((name: string, i: number) => (
                                            <li key={i} style={{ fontSize: '0.8rem', color: '#53575d' }}>{name}</li>
                                          ))}
                                          {entry.metadata.products.names.length > 8 && (
                                            <li style={{ fontSize: '0.8rem', color: '#6f747b', fontStyle: 'italic' }}>
                                              +{entry.metadata.products.names.length - 8} more...
                                            </li>
                                          )}
                                        </ul>
                                      </div>
                                    )}
                                    {/* SKUs if no styles */}
                                    {!entry.metadata.products.styles && entry.metadata.products.skus && entry.metadata.products.skus.length > 0 && (
                                      <div style={{ fontSize: '0.75rem', color: '#6f747b' }}>
                                        SKUs: {entry.metadata.products.skus.join(', ')}
                                      </div>
                                    )}
                                    {/* Color variants count */}
                                    {entry.metadata.products.color_codes && entry.metadata.products.color_codes.length > 0 && (
                                      <div style={{ fontSize: '0.75rem', color: '#6f747b' }}>
                                        Color variants: {entry.metadata.products.color_codes.length}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Feedback Tags & Notes (v2) */}
                              {isEnhanced && entry.feedback_tags && entry.feedback_tags.length > 0 && (
                                <div className="feedback-tags">
                                  <strong>Tags:</strong> {entry.feedback_tags.map(tag => (
                                    <span key={tag} className="tag">{tag}</span>
                                  ))}
                                </div>
                              )}
                              {isEnhanced && entry.feedback_notes && (
                                <div className="feedback-notes">
                                  <strong>Notes:</strong> <em>{entry.feedback_notes}</em>
                                </div>
                              )}
                              
                              {/* Legacy tool calls */}
                              {entry.tool_calls && entry.tool_calls.length > 0 && !isEnhanced && (
                                <div className="tool-calls">
                                  <strong>Tools Used:</strong> {entry.tool_calls.join(', ')}
                                </div>
                              )}
                              
                              {/* Expand/Collapse button for enhanced feedback */}
                              {isEnhanced && ((entry.conversation_history && entry.conversation_history.length > 0) || (entry.tool_calls_detailed && entry.tool_calls_detailed.length > 0)) && (
                                <button
                                  className="expand-button"
                                  onClick={() => {
                                    const newExpanded = new Set(expandedFeedbackIds);
                                    if (isExpanded) {
                                      newExpanded.delete(entry.uid);
                                    } else {
                                      newExpanded.add(entry.uid);
                                    }
                                    setExpandedFeedbackIds(newExpanded);
                                  }}
                                >
                                  {isExpanded ? '▼ Hide Details' : '▶ Show Full Context'}
                                </button>
                              )}
                              
                              {/* Expanded Details */}
                              {isExpanded && isEnhanced && (
                                <div className="expanded-details">
                                  {/* Conversation History */}
                                  {entry.conversation_history && entry.conversation_history.length > 0 && (
                                    <div className="conversation-history">
                                      <h5>
                                        <FileText className="h-4 w-4 inline mr-1" />
                                        Full Conversation ({entry.conversation_history.length} messages)
                                      </h5>
                                      <div className="conversation-messages">
                                        {entry.conversation_history.map((msg, idx) => (
                                          <div key={msg.id || idx} className={`conv-message ${msg.sender}`}>
                                            <div className="conv-header">
                                              <strong>
                                                {msg.sender === 'user' ? (
                                                  <><User className="h-3 w-3 inline mr-1" />User</>
                                                ) : msg.sender === 'agent' ? (
                                                  <><Bot className="h-3 w-3 inline mr-1" />{msg.agentName || 'Agent'}</>
                                                ) : (
                                                  <><MessageCircle className="h-3 w-3 inline mr-1" />System</>
                                                )}
                                              </strong>
                                              <span className="conv-time">{new Date(msg.timestamp).toLocaleTimeString()}</span>
                                            </div>
                                            <div className="conv-content scrollable-content">{msg.content}</div>
                                            
                                            {/* Display product info for this message */}
                                            {msg.metadata && msg.metadata.products && (
                                              <div className="conv-message-metadata" style={{ marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f0fdf4', borderRadius: '0.25rem', border: '1px solid #dcfce7' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.75rem', color: '#047857' }}>
                                                  <Package className="h-3 w-3" />
                                                  Products ({msg.metadata.products.count || msg.metadata.products.styles?.length || 0})
                                                </div>
                                                <div className="metadata-details" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                  {/* Style Codes */}
                                                  {msg.metadata.products.styles && msg.metadata.products.styles.length > 0 && (
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                                      {msg.metadata.products.styles.map((style: string, i: number) => (
                                                        <span key={i} style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '0.125rem 0.25rem', borderRadius: '0.125rem', fontSize: '0.7rem', fontFamily: 'monospace' }}>
                                                          {style}
                                                        </span>
                                                      ))}
                                                    </div>
                                                  )}
                                                  {/* Product Names */}
                                                  {msg.metadata.products.names && msg.metadata.products.names.length > 0 && (
                                                    <div style={{ fontSize: '0.7rem', color: '#53575d' }}>
                                                      {msg.metadata.products.names.slice(0, 5).join(' | ')}{msg.metadata.products.names.length > 5 ? ` +${msg.metadata.products.names.length - 5} more` : ''}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Detailed Tool Calls */}
                                  {entry.tool_calls_detailed && entry.tool_calls_detailed.length > 0 && (
                                    <div className="tool-calls-detailed">
                                      <h5>
                                        <Wrench className="h-4 w-4 inline mr-1" />
                                        Tool Calls ({entry.tool_calls_detailed.length})
                                      </h5>
                                      <div className="tool-calls-list">
                                        {entry.tool_calls_detailed.map((tool, idx) => (
                                          <div key={tool.id || idx} className={`tool-call ${tool.status || 'unknown'}`}>
                                            <div className="tool-header">
                                              <strong>{tool.name}</strong>
                                              {tool.status && (
                                                <span className={`tool-status ${tool.status}`}>
                                                  {tool.status === 'success' ? (
                                                    <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                                  ) : tool.status === 'error' ? (
                                                    <XCircle className="h-3 w-3 inline mr-1" />
                                                  ) : (
                                                    <Loader2 className="h-3 w-3 inline mr-1 animate-spin" />
                                                  )}
                                                  {tool.status}
                                                </span>
                                              )}
                                              {tool.duration_ms && <span className="tool-duration">{tool.duration_ms}ms</span>}
                                            </div>
                                            {tool.arguments && (
                                              <div className="tool-args">
                                                <strong>Arguments:</strong>
                                                <pre>{typeof tool.arguments === 'string' ? tool.arguments.substring(0, 300) : JSON.stringify(tool.arguments, null, 2).substring(0, 300)}</pre>
                                              </div>
                                            )}
                                            {tool.result && (
                                              <div className="tool-result">
                                                <strong>Result:</strong>
                                                <pre>{typeof tool.result === 'string' ? tool.result.substring(0, 300) : JSON.stringify(tool.result, null, 2).substring(0, 300)}</pre>
                                              </div>
                                            )}
                                            {tool.error && (
                                              <div className="tool-error">
                                                <strong>Error:</strong> <code>{tool.error}</code>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="no-feedback">
                    <h4>No Feedback Data</h4>
                    <p>No feedback entries found for {agentConfig.displayName}.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            {isAggregateDashboard ? (
              /* Comprehensive Analytics Dashboard for Aldo - Shows all agents with real App Insights data */
              <ComprehensiveAnalytics agents={allAgents} />
            ) : (
              /* Single-agent Analytics Dashboard - Same style as Aldo but filtered to this agent */
              <ComprehensiveAnalytics 
                agentId={agentConfig.id} 
                agentIcon={agentConfig.icon}
                agentDisplayName={agentConfig.displayName}
                agents={allAgents}
              />
            )}
          </div>
        )}

        {activeTab === 'testing' && agentConfig.hasTestResults && (
          <div className="testing-section">
            
            {/* CSV File Selector */}
            <div className="csv-selector-section">
              <h3>Test Data Source</h3>
              <div className="csv-selector-controls">
                <div className="csv-selector">
                  <label htmlFor="csv-file-select">Select CSV File:</label>
                  <select 
                    id="csv-file-select"
                    value={selectedCsvFile} 
                    onChange={(e) => setSelectedCsvFile(e.target.value)}
                    disabled={loadingCsvFiles || loadingCsvData}
                    className="csv-file-dropdown"
                  >
                    <option value="">Select a table...</option>
                    {availableCsvFiles.map((file) => (
                      <option key={file.filename} value={file.filename}>
                        {file.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <button 
                  onClick={() => {
                    setRealTestResults([]);
                    setCurrentTestRun(undefined);
                    setRefreshTrigger(prev => prev + 1);
                  }}
                  className="csv-refresh-button"
                  disabled={!selectedCsvFile || loadingCsvData}
                >
                  {loadingCsvData ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 inline mr-1" />
                      Refresh Data
                    </>
                  )}
                </button>
              </div>
              
              {csvLoadError && (
                <div className="csv-load-error">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  {csvLoadError}
                </div>
              )}
            </div>

            {testRunError && (
              <div className="error-message">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                {testRunError}
              </div>
            )}
            
            {!selectedCsvFile ? (
              <div className="no-table-selected">
                <div className="placeholder-message">
                  <h4>
                    <BarChart3 className="h-5 w-5 inline mr-2" />
                    No Test Data Loaded
                  </h4>
                  <p>Please select a CSV file from the dropdown above to view test results.</p>
                </div>
              </div>
            ) : loadingCsvData ? (
              <div className="loading-section">
                <div className="loading-content">
                  <div className="loading-spinner"><Loader2 className="h-8 w-8 animate-spin" /></div>
                  <h4>Loading Test Data</h4>
                  <p>Loading {selectedCsvFile}...</p>
                </div>
              </div>
            ) : realTestResults.length > 0 ? (
              <TestResults
                key={`${selectedCsvFile}-${realTestResults.length}-${refreshTrigger}`}
                testRuns={testRuns}
                currentRun={currentTestRun}
                onLoadTestRun={loadTestRun}
                onAnalyzeResults={analyzeTestResults}
              />
            ) : (
              <div className="no-data-message">
                <h4>❌ No Data Available</h4>
                <p>The selected file "{selectedCsvFile}" could not be loaded or contains no data.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'test-review' && agentConfig.hasTestResults && (
          <div className="test-review-section">
            {loadingMostRecent ? (
              <div className="loading-state">
                <div className="loading-spinner">⏳</div>
                <p>Loading most recent test results...</p>
              </div>
            ) : mostRecentTestRun && user ? (
              <TestReviewInterface
                testResults={mostRecentTestRun.results}
                testFileName={mostRecentTestRun.filename}
                agentConfig={agentConfig}
                onClose={() => setActiveTab('testing')}
                onComplete={() => setActiveTab('testing')}
              />
            ) : (
              <div className="no-test-review-data">
                <h3>
                  <Search className="h-5 w-5 inline mr-2" />
                  No Test Data Available
                </h3>
                <p>No recent test results found for review. Please run tests first in the Test Results tab.</p>
                <button 
                  className="switch-tab-btn"
                  onClick={() => setActiveTab('testing')}
                >
                  Go to Test Results
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'indexing' && isSuperAdmin && (
          <div className="management-section">
            <IndexingDashboard embedded />
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentDashboard;